// apps/web/src/pages/spread/settings/SharingTab.tsx
//
// Phase 9 Plan 09-12 Task 2 — Sharing tab of the SpreadSettingsDialog.
//
// COMPOSITION PER UI-SPEC §"Sharing tab" (lines 414-429):
//   - Visibility radio group bound to spread form path `visibility`. Values:
//     private / public / shared / group (D-11 default `private`).
//   - Section header "PEOPLE WITH ACCESS" + GrantsList component (live list).
//   - Add-grant row: <Input email> + <Add> button that POSTs to Phase 5's
//     /api/spreads/:id/grants/user endpoint via TanStack Mutation.
//
// IMPORTANT — Q-07 DECOUPLING:
//   Grant changes do NOT travel through the spread save body (Q-07 atomic
//   PUT carries the spread itself; grants are a separate Phase 5 surface).
//   The Add and Revoke calls hit /api/spreads/:id/grants/* directly. The
//   list invalidates on success so the UI stays consistent without a
//   round-trip through the spread form's dirty state.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { api } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useTerm } from "@/lib/vocabulary/useTerm";
import { GrantsList } from "./GrantsList";

const VISIBILITY_OPTIONS = ["private", "public", "shared", "group"] as const;

export interface SharingTabProps {
  /** Spread id. Empty string when authoring a brand-new spread that hasn't been
   * saved yet — the tab still renders the visibility radio (which IS part of
   * the spread save body), but the grants surface is gated behind an existence
   * check (you cannot grant access to a not-yet-persisted resource). */
  spreadId: string;
}

export function SharingTab({ spreadId }: SharingTabProps): React.ReactElement {
  const { control } = useFormContext();
  const qc = useQueryClient();
  const container = useTerm("container");
  const [pendingEmail, setPendingEmail] = useState("");

  const isPersisted = spreadId.length > 0 && spreadId !== "new-spread";

  const addGrantMutation = useMutation<unknown, Error, string>({
    mutationFn: async (subjectUserId) => {
      return api<unknown>(`/api/spreads/${spreadId}/grants/user`, {
        method: "POST",
        body: JSON.stringify({ subjectUserId, permission: "read" }),
      });
    },
    onSuccess: () => {
      setPendingEmail("");
      void qc.invalidateQueries({ queryKey: ["spread", spreadId, "grants"] });
    },
  });

  function handleAddClick(): void {
    if (pendingEmail.trim().length === 0) return;
    // For v1 we send the raw email as subjectUserId — Phase 5's create-grant
    // endpoint accepts subjectUserId as a text field (Better-Auth user.id is
    // text). A future Phase 9 polish layer will resolve email -> userId via
    // a /api/users/by-email lookup; until then the integration test path is
    // raw-userId-only.
    addGrantMutation.mutate(pendingEmail.trim());
  }

  return (
    <div className="flex flex-col gap-6">
      {/* ─── Visibility radio (form-state path: `visibility`) ─── */}
      <FormField
        control={control}
        name="visibility"
        render={({ field }) => (
          <FormItem data-field-path="visibility">
            <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              VISIBILITY
            </FormLabel>
            <FormControl>
              <div role="radiogroup" className="flex flex-col gap-2">
                {VISIBILITY_OPTIONS.map((opt) => (
                  <label
                    key={opt}
                    className="flex items-center gap-3 rounded-md border border-input bg-card p-3 hover:bg-accent"
                  >
                    <input
                      type="radio"
                      name="visibility"
                      value={opt}
                      checked={field.value === opt}
                      onChange={() => field.onChange(opt)}
                      aria-label={visibilityLabel(opt, container)}
                    />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">
                        {visibilityLabel(opt, container)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {visibilityHelper(opt, container)}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* ─── Grants section (Phase 5 endpoints, NOT spread save body) ─── */}
      <section className="flex flex-col gap-3" data-section="grants">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          PEOPLE WITH ACCESS
        </h3>
        {isPersisted ? (
          <>
            <GrantsList spreadId={spreadId} />
            <div className="flex items-center gap-2">
              <Input
                value={pendingEmail}
                onChange={(e) => setPendingEmail(e.target.value)}
                placeholder="Email address or username"
                aria-label="Add user by email or username"
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleAddClick}
                disabled={pendingEmail.trim().length === 0 || addGrantMutation.isPending}
              >
                Add
              </Button>
            </div>
            {addGrantMutation.isError ? (
              <p className="text-xs text-destructive">
                Couldn't add that user.{" "}
                {addGrantMutation.error instanceof Error
                  ? addGrantMutation.error.message
                  : ""}
              </p>
            ) : null}
          </>
        ) : (
          <p className="text-xs text-muted-foreground">
            Save the {container.toLowerCase()} once before granting access.
          </p>
        )}
      </section>
    </div>
  );
}
SharingTab.displayName = "SharingTab";

function visibilityLabel(opt: (typeof VISIBILITY_OPTIONS)[number], container: string): string {
  switch (opt) {
    case "private":
      return "Private";
    case "public":
      return "Public";
    case "shared":
      return "Shared with specific people";
    case "group":
      return "Shared with a group";
  }
}

function visibilityHelper(opt: (typeof VISIBILITY_OPTIONS)[number], container: string): string {
  switch (opt) {
    case "private":
      return `Only you can see this ${container.toLowerCase()}.`;
    case "public":
      return `Anyone with the link can view this ${container.toLowerCase()}.`;
    case "shared":
      return "Add users below.";
    case "group":
      return "Select a group below.";
  }
}
